import Header from "../../../components/common/Header";
import {useNavigate, useParams} from "react-router-dom";
import useDeceased from "../../../hooks/useDeceased";
import {getDeceasedInfos} from "../../../api/pro/deceasedReq";
import useAuth from "../../../hooks/useAuth";
import {useEffect, useRef, useState} from "react";
import {
    getCadExpressList,
    getCadExpressListByDeceased,
    printCadsExpress,
    saveCadListForDeceased
} from "../../../api/pro/cadExpressReq";
import {downloadCadMemo} from "../../../api/pro/eventReq";
import TiptapView from "../../../components/common/input-layout/tiptap/TiptapView";
import Icon from "../../../components/common/Icon";
import Missing from "../../Missing";

export default function CADExpress() {
    const {deceasedInfos, setDeceasedInfos} = useDeceased();
    const {id} = useParams();
    const {auth} = useAuth();
    const token = auth.token;
    const navigate = useNavigate();
    const [cadExpressList, setCadExpressList] = useState();
    const [enabledCadExpressList, setEnabledCadExpressList] = useState([]);
    const [deceasedCadList, setDeceasedCadList] = useState([]);
    const [optSelected, setOptSelected] = useState([]);
    const [optChecked, setOptChecked] = useState([]);
    const [openCategories, setOpenCategories] = useState([1, 2, 3, 4]); // Toutes les catégories ouvertes par défaut
    const [saveMessage, setSaveMessage] = useState(null);
    const catRef = useRef();

    const apiDeceasedInfos = async () => {
        const response = await getDeceasedInfos(token, id)
        setDeceasedInfos(response)
    }

    const apiCadExpressList = async () => {
        const response = await getCadExpressList(token)
        setCadExpressList(response)
    }

    const apiDeceasedCadList = async () => {
        const response = await getCadExpressListByDeceased(token, id)
        setDeceasedCadList(response)
        setOptSelected([]);
        setOptChecked(response.map(cad => cad?.number));
        for (let cad of response) {
            setOptSelected(prevState => [
                ...prevState,
                ...Array.from({length: cad?.quantity}, () => (cad?.number).toString())
            ])
        }
    }

    useEffect(() => {
        apiDeceasedCadList()
        apiCadExpressList()
        apiDeceasedInfos()
    }, [ id ]);

    useEffect(() => {
        if (cadExpressList) {
            setEnabledCadExpressList(cadExpressList?.filter(cad => cad?.enabled == 1))
        }
    }, [ cadExpressList ]);

    const handlePlusOpt = (optId) => {
        setOptSelected(prevState => [...prevState, optId])
        setOptChecked(prevState => prevState.includes(optId) ? prevState : [...prevState, optId])
    }

    const handleMinusOpt = (optId) => {
        let optToDelete = optSelected.indexOf(optId)
        let newOptSelected = [...optSelected]
        newOptSelected.splice(optToDelete, 1)
        setOptSelected(newOptSelected)
    }

    const handleQuantityChange = (cadNumber, newQuantity) => {
        const quantity = parseInt(newQuantity, 10)
        if (isNaN(quantity) || quantity < 0) return
        
        // Filtrer pour garder tous les courriers sauf celui qu'on modifie
        const newOptSelected = optSelected.filter(opt => opt !== cadNumber)
        
        // Ajouter le nombre correct d'occurrences du courrier
        for (let i = 0; i < quantity; i++) {
            newOptSelected.push(cadNumber)
        }
        
        setOptSelected(newOptSelected)
    }

    const handleToggleCatView = (e, categoryId) => {
        e.preventDefault()
        e.stopPropagation()
        setOpenCategories(prev => 
            prev.includes(categoryId) 
                ? prev.filter(id => id !== categoryId)
                : [...prev, categoryId]
        )
    }

    const categories = [
        {id: 1, title: "Courriers à envoyer", boldPart: "DANS LES 48 HEURES"},
        {id: 2, title: "Courriers à envoyer", boldPart: "DANS LA SEMAINE"},
        {id: 3, title: "Courriers à envoyer", boldPart: "DANS LE MOIS AU PLUS TARD"},
        {id: 4, title: "Courriers à envoyer", boldPart: "DANS LES 6 MOIS AU PLUS TARD"}
    ];

    const handleMemoCad = () => {
        downloadCadMemo(token, id)
            .then(response => {
                if (response instanceof Blob && response.type === 'text/html') {
                    response.text().then(downloadUrl => {
                        const pdfLink = document.createElement('a');
                        pdfLink.href = downloadUrl;
                        pdfLink.target = '_blank';
                        pdfLink.setAttribute('download', "memo_courriers.pdf");
                        document.body.appendChild(pdfLink);
                        pdfLink.click();
                        document.body.removeChild(pdfLink);
                    });
                }
            })
    }

    const cleanIds = optSelected.reduce((acc, nb) => {
        if (acc[nb]) {
            acc[nb]++;
        } else {
            acc[nb] = 1;
        }
        return acc;
    }, {});

    const checkedAndSelected = Object.keys(cleanIds)
        .filter(key => optChecked.includes(key))
        .reduce((res, key) => {
            res[key] = cleanIds[key];
            return res;
        }, {});

    const handleDeleteAllCad = (cadId) => {
        let newOptSelected = [...optSelected]
        let index = newOptSelected.indexOf(cadId)
        while (index !== -1) {
            newOptSelected.splice(index, 1)
            index = newOptSelected.indexOf(cadId)
        }
        setOptSelected(newOptSelected)
        setOptChecked(prevState => prevState.filter(opt => opt !== cadId))
    }

    const handleCadsPrint = () => {
        const validCadIds = {};

        Object.keys(checkedAndSelected).forEach(key => {
            const numericKey = parseInt(key, 10);
            const matchingCad = enabledCadExpressList?.find(cad =>
                parseInt(cad.number, 10) === numericKey || cad.number === key
            );

            if (matchingCad) {
                validCadIds[matchingCad.number] = checkedAndSelected[key];
            }
        });

        if (Object.keys(validCadIds).length === 0) {
            alert('Aucun courrier valide sélectionné pour l\'impression.');
            return;
        }

        let body = {
            deceasedId: parseInt(id, 10),
            cadIds: validCadIds,
        }

        printCadsExpress(token, body)
            .then(response => {
                if (response instanceof Blob && response.type === 'text/html') {
                    response.text().then(downloadUrl => {
                        const pdfLink = document.createElement('a');
                        pdfLink.href = downloadUrl;
                        pdfLink.target = '_blank';
                        pdfLink.setAttribute('download', "courriers.pdf");
                        document.body.appendChild(pdfLink);
                        pdfLink.click();
                        document.body.removeChild(pdfLink);
                    });
                }
            })
    }

    const handleCadsSave = () => {
        let body = {
            deceasedId: id,
            cadIds: cleanIds,
        }
        saveCadListForDeceased(token, body)
            .then(() => {
                setSaveMessage(`La liste a bien été enregistrée${optChecked.length === 0 ? " (aucun élément coché)" : ""}.`);
                setTimeout(() => setSaveMessage(null), 3000);
            })
    }

    const handleCheckOpt = (e,number) => {
        if (e.target.checked) {
            setOptChecked([...optChecked, number])
        } else {
            let newOptChecked = [...optChecked]
            let index = newOptChecked.indexOf(number)
            newOptChecked.splice(index, 1)
            setOptChecked(newOptChecked)
        }
    }

    const handleCheckAll = (e) => {
        if (e.target.checked) {
            setOptChecked(optSelected)
        } else {
            setOptChecked([])
        }
    }

    const handleDeleteList = () => {
        setOptSelected([])
        setOptChecked([])
    }

    const calculateAge = (birthDate, deathDate) => {
        if (!birthDate || !deathDate) return null;

        const birth = new Date(birthDate);
        const death = new Date(deathDate);

        let age = death.getFullYear() - birth.getFullYear();
        const monthDiff = death.getMonth() - birth.getMonth();

        if (monthDiff < 0 || (monthDiff === 0 && death.getDate() < birth.getDate())) {
            age--;
        }

        return age;
    };
    const age = calculateAge(deceasedInfos?.birthDate, deceasedInfos?.deathDate);


    const CadSection = ({category, title, boldPart}) => (
        <div id={`cat-${category}`} className={`events cad ${openCategories.includes(category) ? 'active' : ''}`}>
            <h2>{title} <b>{boldPart}</b></h2>
            <ul>
                {enabledCadExpressList?.filter(cad => cad?.category === category).map((cad, index) => (
                        <li key={index}>
                            <i className="fa fa-question-circle">
                                <div className="description-overlay html-textarea view tiptap">
                                    <TiptapView content={cad.description} />
                                </div>
                            </i>
                            { (optSelected.includes(cad?.number)) && <i onClick={() => handleMinusOpt(cad.number)} className="fa fa-minus" />}
                            <strong>{cad.name}</strong>
                            <i onClick={() => handlePlusOpt(cad?.number)} className="fa fa-plus"/>
                        </li>
                ))}
            </ul>
            <div className="arrow" onClick={(e) => handleToggleCatView(e, category)}>
                <i className="fa fa-chevron-up"/>
            </div>
        </div>
    );

    const Form = () => (
        <div ref={catRef} className="column content-container cad">
            <Icon source="fa fa-envelope" span="Courriers après décès Express" />
            <span className="title">{!deceasedInfos?.hiddenCivility && (deceasedInfos?.civility == "MME" ? "Madame " : "Monsieur ")}{deceasedInfos?.lastName} {deceasedInfos?.firstName} {age !== null && `(${age} ans)`}</span>

            {categories.map(category => (
                <CadSection key={category.id} category={category.id} title={category.title} boldPart={category.boldPart} />
            ))}
        </div>
    );

    return (
        <main>
            {deceasedInfos?.error &&
                <Missing />
            }
            { !deceasedInfos?.error &&
            <>
            <Header
                location="Espace de gestion des courriers après décès"
                locationComp={`
                    ${deceasedInfos?.lastName} 
                    ${deceasedInfos?.firstName}
                `}
                parent="Défunt"
                slug={`/defunt/${deceasedInfos?.id}`}
            />

                <div className="double-fieldset">
                <div className="cad choice">
                    <Form/>
                </div>
                    <div className="cad detail-box">
                        <legend>Liste d'impression</legend>
                        <button className="green-button" onClick={handleMemoCad}>
                            <i className="fa fa-print"/>
                            <span>Récap courrier</span>
                        </button>
                        <ul>
                            <div className="flex between">
                                <span>
                                    <input type="checkbox" onChange={handleCheckAll} id="selectall" name="selectall"/>
                                    <span htmlFor="selectall">Tout sélectionner</span>
                                </span>
                                <span onClick={handleDeleteList}><i className="fa fa-close red-text" />Tout supprimer</span>
                            </div>
                            { (enabledCadExpressList?.filter(cad => optSelected.includes(cad?.number))?.length > 0) ? enabledCadExpressList?.filter(cad => optSelected.includes(cad?.number)).map(cad => (
                                <li key={cad.number} className="cad-list-item">
                                    <input 
                                        name={`${cad?.number}-check`} 
                                        checked={optChecked?.includes(cad?.number)} 
                                        type="checkbox" 
                                        onChange={(e) => handleCheckOpt(e,cad?.number)}
                                    />
                                    <div className="cad-item-content">
                                        <i className="fa fa-minus" onClick={() => handleMinusOpt(cad.number)} />
                                        <span className="cad-name">{cad?.name}</span>
                                        <input 
                                            type="number" 
                                            className="cad-quantity"
                                            min="0"
                                            value={optSelected.filter(opt => (opt === cad?.number)).length}
                                            onChange={(e) => handleQuantityChange(cad.number, e.target.value)}
                                        />
                                        <i className="fa fa-plus" onClick={() => handlePlusOpt(cad?.number)} />
                                    </div>
                                    <i className="fa fa-close cad-delete" onClick={() => handleDeleteAllCad(cad?.number)}/>
                                </li>
                                )) :
                                <span>Aucun élément n'a été ajouté</span>
                            }
                        </ul>
                        <button className="blue-button" onClick={handleCadsSave}>
                            <i className="fa fa-save"/>
                            <span>Enregistrer la liste</span>
                        </button>
                        <button className="green-button" onClick={handleCadsPrint}>
                            <i className="fa fa-print"/>
                            <span>Imprimer</span>
                        </button>
                        {saveMessage &&
                            <div className="alert alert-success">{saveMessage}</div>
                        }
                    </div>
                </div>
                <div className="buttons-container back bottom-centered">
                    <button type="button" onClick={() => navigate(-1)}>
                        <i className="fa fa-arrow-left"></i>
                        <span>Retour</span>
                    </button>
                </div>
            </>
            }
        </main>
    )
}
